import React, { useState } from 'react';
import JsonView from 'react18-json-view';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  useToast,
  VStack,
} from '@chakra-ui/react';
import 'react18-json-view/src/style.css';
import { GET_FEATURE_FLAGS } from '../../utils/constants';
import { getFirebaseIdToken } from '../../utils/utils';

const GetFeatureFlags = () => {
  const [publicKey, setPublicKey] = useState('');
  const [featureFlagNames, setFeatureFlagNames] = useState('');
  const [appId, setAppId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [featureFlagsData, setFeatureFlagsData] = useState<any>(null);

  const toast = useToast();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!publicKey.trim()) {
      newErrors.publicKey = 'Public Key is required';
    }

    if (!featureFlagNames.trim()) {
      newErrors.featureFlagNames = 'Feature Flag Names are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      let url = GET_FEATURE_FLAGS;

      // Add required parameters
      url += `publicKey=${encodeURIComponent(publicKey)}`;
      url += `&${featureFlagNames
        .split(',')
        .map((name) => `featureFlagNames=${encodeURIComponent(name.trim())}`)
        .join('&')}`;

      // Add optional parameters if they exist
      if (appId) {
        url += `&appId=${encodeURIComponent(appId)}`;
      }

      if (providerId) {
        url += `&providerId=${encodeURIComponent(providerId)}`;
      }

      if (sessionId) {
        url += `&sessionId=${encodeURIComponent(sessionId)}`;
      }

      const accessToken = await getFirebaseIdToken();

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to retrieve feature flags');
      }

      const data = await response.json();
      setFeatureFlagsData(data);

      toast({
        title: 'Success',
        description: 'Feature flags retrieved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      console.error('Error fetching feature flags:', error);
      toast({
        title: 'Error',
        description: 'Failed to retrieve feature flags',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFeatureFlagsData(null);
    setPublicKey('');
    setFeatureFlagNames('');
    setAppId('');
    setProviderId('');
    setSessionId('');
  };

  return (
    <>
      {featureFlagsData ? (
        <>
          <Flex direction="column" gap={4} align="center">
            <Button
              colorScheme="blackAlpha"
              bgColor="blackAlpha.800"
              _hover={{ transform: 'scale(1.02)' }}
              onClick={handleClear}
              size="sm"
            >
              New Search
            </Button>
            <Box p={4} borderRadius="md" w="100%" bgColor="gray.50" minH="300px">
              <JsonView
                theme="a11y"
                displaySize="expanded"
                enableClipboard={false}
                src={featureFlagsData}
              />
            </Box>
          </Flex>
        </>
      ) : (
        <Box as="form" onSubmit={handleSubmit}>
          <VStack spacing={4}>
            <FormControl isInvalid={!!errors.publicKey} isRequired>
              <FormLabel>Public Key</FormLabel>
              <Input
                value={publicKey}
                onChange={(e) => setPublicKey(e.target.value)}
                placeholder="Enter public key"
              />
              <FormErrorMessage>{errors.publicKey}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.featureFlagNames} isRequired>
              <FormLabel>Feature Flag Names</FormLabel>
              <Input
                value={featureFlagNames}
                onChange={(e) => setFeatureFlagNames(e.target.value)}
                placeholder="Enter feature flag names (comma-separated)"
              />
              <FormErrorMessage>{errors.featureFlagNames}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>App ID</FormLabel>
              <Input
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
                placeholder="Enter app ID (optional)"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Provider ID</FormLabel>
              <Input
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
                placeholder="Enter provider ID (optional)"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Session ID</FormLabel>
              <Input
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                placeholder="Enter session ID (optional)"
              />
            </FormControl>

            <Button type="submit" colorScheme="blue" isLoading={loading}>
              Get Feature Flags
            </Button>
          </VStack>
        </Box>
      )}
    </>
  );
};

export default GetFeatureFlags;
